/**
 * RFID-Etiketten (bentomax/hawk#605): XID = aufgedruckter Text `XID-NNNNNNN`,
 * EPC = daraus berechneter Chip-Inhalt `58` + 7 Ziffern + 15 Nullen.
 *
 * Gleiche Logik wie `rails/app/services/xid.rb` und (Schritt 2) `app/utils/xid.ts`,
 * gleiche Testfälle. Gespeichert wird immer die Normalform; die DB prüft streng.
 */

const XID_PATTERN = /^XID-?(\d{7})$/
const DIGITS_PATTERN = /^(\d{7})$/
// Nur Bentomax-EPCs: Header 58, 7 Ziffern, 15 Nullen – fremde Chips ergeben null
const EPC_PATTERN = /^58(\d{7})0{15}$/

/**
 * Tolerante Normalisierung: Leerzeichen raus, Großschreibung. Akzeptiert
 * `XID-0101010`, `xid-0101010`, `XID0101010`, `XID 0101010`, `0101010` und
 * einen gescannten EPC – so taugt dasselbe Feld für QR-Code und RFID-Scan.
 * Alles andere ergibt `null`.
 */
export function normalizeXid(input: string | null | undefined): string | null {
  if (typeof input !== 'string') return null

  const value = input.replace(/\s+/g, '').toUpperCase()
  const digits = XID_PATTERN.exec(value)?.[1] ?? DIGITS_PATTERN.exec(value)?.[1]
  if (digits) return `XID-${digits}`

  return xidFromEpc(value)
}

/** EPC zur XID; `null`, wenn die Eingabe keine gültige XID ist. */
export function xidToEpc(xid: string | null | undefined): string | null {
  const normalized = normalizeXid(xid)
  if (!normalized) return null

  return `58${normalized.slice(4)}${'0'.repeat(15)}`
}

/**
 * Hinweis auf ein ersetztes Etikett aus einer Suchantwort. Rails hängt
 * `xid_hint` an jeden Treffer (bzw. an die leere Antwort der Einzelsuche),
 * wenn nach einer gesperrten XID gesucht wurde.
 */
export function xidHintOf(response: unknown): string | null {
  const rows = Array.isArray(response) ? response : [response]
  const hint = rows.find((row) => typeof row?.xid_hint === 'string' && row.xid_hint)?.xid_hint
  return hint ?? null
}

/** XID zum EPC (Groß/klein egal); fremde EPCs ergeben `null`. */
export function xidFromEpc(epc: string | null | undefined): string | null {
  if (typeof epc !== 'string') return null

  const digits = EPC_PATTERN.exec(epc.trim().toUpperCase())?.[1]
  return digits ? `XID-${digits}` : null
}
